import { Button, Image } from "antd";
import { useEffect, useRef, useState } from "react";
import UploadIcon from "../../../assets/icons/UploadIcon";
import XIcon from "../../../assets/icons/XIcon";
import { useFileUploadMutation } from "../../../features/superAdmin/employeeService/employeeManagementService";
import { useUpdateGroupPhotoMutation } from "../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import CustomModal from "../../ui/CustomModal";

type AddGroupPhotoModalProps = ModalComponentProps & {
  handleParentClose?: () => void;
  chatId?: number;
};

export default function AddGroupPhotoModal({
  handleClose,
  handleParentClose,
  chatId,
  ...rest
}: AddGroupPhotoModalProps) {
  const [attachment, setAttachment] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [
    fileUpload,
    {
      error: fileUploadError,
      isError: fileUploadIsError,
      isLoading: isFileUploading,
    },
  ] = useFileUploadMutation();
  const [
    updateGroupPhoto,
    {
      isSuccess: isUpdateGroupPhotoSuccess,
      isError: isUpdateGroupPhotoError,
      error: updateGroupPhotoError,
      isLoading: isUpdateGroupPhotoLoading,
    },
  ] = useUpdateGroupPhotoMutation();
  useError(fileUploadIsError, fileUploadError, "Upload photo fail");
  useError(
    isUpdateGroupPhotoError,
    updateGroupPhotoError,
    "Group photo add fail"
  );

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxFileSize = 30 * 1024 * 1024;

    if (file.size > maxFileSize) {
      toast("error", `"${file.name}" exceeds max file size of 30MB.`);
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    try {
      const res: any = await fileUpload(formData).unwrap();
      if (res?.Location) {
        setAttachment(res.Location);
      }
    } catch (err) {
      console.error(`Upload failed for ${file.name}`, err);
    }

    e.target.value = "";
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleUpdatePhoto = () => {
    const newData = {
      photo: attachment,
    };

    updateGroupPhoto({
      id: chatId,
      data: newData,
    });
  };

  useEffect(() => {
    if (isUpdateGroupPhotoSuccess) {
      toast("success", "Group photo added successfully!");
      setAttachment("");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateGroupPhotoSuccess]);

  return (
    <CustomModal
      title="Add Group Photo"
      {...rest}
      handleClose={handleClose}
      width={360}
      footer={null}
    >
      <div className="flex flex-col items-center justify-center mt-4 gap-4">
        {attachment && (
          <div className="relative">
            <Image
              src={attachment}
              className="rounded-full object-cover"
              alt="Uploaded Group"
              width={80}
              height={80}
            />
            <span
              className="absolute -top-1 -right-1 bg-white p-1 rounded-full shadow cursor-pointer"
              onClick={() => setAttachment("")}
            >
              <XIcon />
            </span>
          </div>
        )}

        <div>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleImageChange}
            accept="image/*"
          />
          <Button
            icon={<UploadIcon />}
            loading={isFileUploading}
            onClick={triggerFileInput}
          >
            Upload Photo
          </Button>
        </div>
      </div>

      <div className="py-2 flex justify-between">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          loading={isUpdateGroupPhotoLoading}
          onClick={handleUpdatePhoto}
          disabled={!attachment}
          className="bg-[#FFF1EB] text-[#FE7235] border-none"
        >
          Add
        </Button>
      </div>
    </CustomModal>
  );
}
